import { useState, useEffect } from 'react';
import API from '../../api/axiosConfig';
import KsaForm from './KsaForm';
import { getCurrentPeriod } from "../../Utils/getCurrentPeriod";

const GRADE_STYLE = {
  'A+': { bg: '#dcfce7', color: '#15803d' },
  'A':  { bg: '#f0fdf4', color: '#16a34a' },
  'B':  { bg: '#dbeafe', color: '#1d4ed8' },
  'C':  { bg: '#fef9c3', color: '#a16207' },
  'D':  { bg: '#ffedd5', color: '#c2410c' },
  'F':  { bg: '#fee2e2', color: '#b91c1c' },
};

const ScoreBar = ({ value, color = '#3b82f6' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <div style={{ flex: 1, backgroundColor: '#f3f4f6', borderRadius: 999, height: 6 }}>
      <div style={{ width: `${value ?? 0}%`, height: 6, borderRadius: 999, backgroundColor: color, transition: 'width 0.3s' }} />
    </div>
    <span style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)', width: 28 }}>{value ?? '—'}</span>
  </div>
);

const cardStyle = {
  backgroundColor: 'var(--background-color)',
  border: '1px solid var(--border-color)',
  borderRadius: '12px',
  padding: '16px',
  textAlign: 'center',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
};

const KsaList = ({ period = getCurrentPeriod() }) => {
  const [data,    setData]    = useState({ data: [], stats: {}, meta: {} });
  const [loading, setLoading] = useState(true);
  const [showForm,setShowForm]= useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await API.get('/ksa-assessments', { params: { period } });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [period]);

  const handleDelete = async (id) => {
    if (!window.confirm('هل تريد حذف هذا التقييم؟')) return;
    await API.delete(`/ksa-assessments/${id}`);
    load();
  };

  const stats = data.stats ?? {};

  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }} dir="rtl">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 style={{ color: 'var(--text-color)', fontWeight: 600, marginBottom: 4 }}>تقييمات KSA</h2>
          <p style={{ color: 'var(--text-muted, #6b7280)', fontSize: 13, margin: 0 }}>
            المعرفة • المهارات • السلوك — الفترة: {period}
          </p>
        </div>
        <button
          onClick={() => { setShowForm(s => !s); setEditing(null); }}
          style={{ backgroundColor: '#7c3aed', color: '#fff', border: 'none', borderRadius: '10px', padding: '8px 18px', fontSize: 14, cursor: 'pointer' }}
        >
          {showForm ? 'إغلاق' : '+ تقييم جديد'}
        </button>
      </div>

      {/* Form */}
      {(showForm || editing) && (
        <div className="mb-4">
          <KsaForm period={period} initialData={editing}
            onSuccess={() => { setShowForm(false); setEditing(null); load(); }} />
        </div>
      )}

      {/* Stats */}
      <div className="row g-3 mb-4">
        {[
          { label: 'إجمالي التقييمات', value: stats.total          ?? 0,   color: '#1f2937' },
          { label: 'متوسط المعرفة',    value: stats.avg_knowledge  ?? '—', color: '#2563eb' },
          { label: 'متوسط المهارات',   value: stats.avg_skills     ?? '—', color: '#0f766e' },
          { label: 'متوسط السلوك',     value: stats.avg_attitude   ?? '—', color: '#7c3aed' },
        ].map(s => (
          <div className="col-6 col-sm-3" key={s.label}>
            <div style={cardStyle}>
              <p style={{ fontSize: 24, fontWeight: 700, color: s.color, margin: 0 }}>{s.value}</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)', marginTop: 4 }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Grade Distribution */}
      {Object.keys(stats.grade_distribution ?? {}).length > 0 && (
        <div style={{ ...cardStyle, textAlign: 'right', marginBottom: 16 }}>
          <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-color)', marginBottom: 10 }}>توزيع التقديرات</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {Object.entries(stats.grade_distribution).map(([grade, count]) => {
              const s = GRADE_STYLE[grade] ?? { bg: '#f3f4f6', color: '#374151' };
              return (
                <span key={grade} style={{ backgroundColor: s.bg, color: s.color, padding: '4px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600 }}>
                  {grade}: {count}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted, #9ca3af)' }}>جاري التحميل...</div>
      ) : data.data.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted, #9ca3af)', backgroundColor: 'var(--background-color)', borderRadius: 12 }}>
          <div style={{ fontSize: 40 }}>🎯</div>
          <p>لا توجد تقييمات لهذه الفترة</p>
        </div>
      ) : (
        <div style={{ backgroundColor: 'var(--background-color)', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border-color)' }}>
          <table className="table mb-0" style={{ color: 'var(--text-color)' }}>
            <thead style={{ backgroundColor: 'var(--border-color)', color: 'var(--text-color)' }}>
              <tr>
                {['الموظف','المعرفة','المهارات','السلوك','الإجمالي','التقدير','إجراءات'].map((h, i) => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: i === 0 ? 'right' : 'center', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.data.map(row => {
                const g = GRADE_STYLE[row.grade] ?? { bg: '#f3f4f6', color: '#374151' };
                return (
                  <tr key={row.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 500 }}>
                      <div>{row.employee_name ?? '—'}</div>
                      {row.assessor_name && <div style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)' }}>المقيّم: {row.assessor_name}</div>}
                    </td>
                    <td style={{ padding: '12px 16px' }}><ScoreBar value={row.knowledge_score} color="#2563eb" /></td>
                    <td style={{ padding: '12px 16px' }}><ScoreBar value={row.skills_score}    color="#0f766e" /></td>
                    <td style={{ padding: '12px 16px' }}><ScoreBar value={row.attitude_score}  color="#7c3aed" /></td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700 }}>{row.total_score ?? '—'}%</td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{ backgroundColor: g.bg, color: g.color, padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>
                        {row.grade} — {row.grade_label}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <button onClick={() => { setEditing(row); setShowForm(false); }}
                        style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: 12, cursor: 'pointer', marginLeft: 8 }}>تعديل</button>
                      <button onClick={() => handleDelete(row.id)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 12, cursor: 'pointer' }}>حذف</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default KsaList;